import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import omit from 'lodash/omit';

import {
  IsBoolean,
  IsInt,
  IsHexColor,
  IsNotEmpty,
  IsNumber,
  IsOptional,
  IsString,
  Max,
  Min,
  ValidateIf,
  ValidateNested,
  IsArray,
  IsEnum,
  MaxLength,
  Matches,
} from 'class-validator';
import { Type } from 'class-transformer';

import { BusinessOfficeHours } from 'src/models/BusinessHours/BusinessHours';
import createCollectionResource from 'src/actions/shared/createResource';
import getTypeDependsOnWidgetType from 'src/models/helpers/getTypeDependsOnWidgetType';
import validateChatQuickActions from 'src/actions/shared/validateChatQuickActions';
import validateQuickReply from 'src/actions/shared/validateQuickReply';
import validateType, { translateErrors } from 'src/adapters/validateType';
import { ActionValueIsValid, QuickActionsAreValid } from './quickActionValidator';
import { DomainError as DomainErrorInterface } from 'src/models/DomainError';
import { DomainError } from '@ad-scripts/application/lib/error';
import {
  ICON_TEXT,
  SELF_SERVICE_WIDGET_TYPE,
  CHAT_WIDGET_TYPE,
} from 'src/presentation/components/pages/chat/chatConstants';
import { toastError } from 'src/actions/toast';
import * as sharedTypes from 'src/actions/shared/sharedTypes';
import { EndpointTypes } from 'src/models/Channels/endpointType';

enum ALIGNMENT {
  LEFT = 'LEFT',
  RIGHT = 'RIGHT',
}
enum BORDER_RADIUS {
  NONE = 'NONE',
  SMALL = 'SMALL',
  LARGE = 'LARGE',
}
enum COMPOSITION_STAGE {
  NAME = 'NAME',
  EMAIL = 'EMAIL',
}

enum USER_RESPONSE_TYPE {
  TEXT = 'TEXT',
  QUICK_REPLY_REQUEST = 'QUICK_REPLY_REQUEST',
}

enum URL_RULE_MATCH_TYPE {
  CONTAINS = 'CONTAINS',
  EXACT = 'EXACT',
  REGEX = 'REGEX',
}
enum RULE_TYPE {
  URL = 'URL',
}

enum TARGET_TYPE {
  AGENT = 'AGENT',
  AUTOMATION = 'AUTOMATION',
}

export class Endpoint {
  id: string;
  type: string;
  address: string;
  displayName: string;
  accountId: string;
  name: string;

  @ValidateNested()
  @Type((typeHeper: any) =>
    typeHeper?.object?.type === EndpointTypes.CUSTOM_CHANNEL ? CustomChannelConfiguration : Configuration
  )
  configuration: CustomChannelConfiguration | Configuration;

  @ValidateNested()
  @Type(() => MessageAutomationConfiguration)
  messageAutomationConfiguration: MessageAutomationConfiguration;
}

class CustomText {
  @ValidateNested()
  @Type(() => ChatHistory)
  chatHistory: ChatHistory;

  @ValidateNested()
  @Type(() => Composition)
  composition: Composition;

  @ValidateNested()
  @Type(() => CustomTextHeaderForChat)
  header: CustomTextHeaderForChat;

  @ValidateNested()
  @Type(() => OnboardingDisabled)
  interactiveOnboarding: OnboardingDisabled;
}

class CustomTextForChat extends CustomText {
  @ValidateNested()
  @Type(() => OnboardingEnabled)
  interactiveOnboarding: OnboardingEnabled;
}

class CustomTextForSelfService extends CustomText {
  @IsString()
  @IsNotEmpty()
  buttonText: string;

  @IsString()
  @IsNotEmpty()
  inactiveButtonText: string;

  @ValidateNested()
  @Type(() => CustomTextHeader)
  header: CustomTextHeader;

  @IsString()
  @IsNotEmpty()
  officeHourMessage: string;

  @ValidateNested()
  @Type(() => OnboardingEnabled)
  interactiveOnboarding: OnboardingEnabled | OnboardingDisabled;
}

class CustomTextForSelfServiceWithoutOnboarding extends CustomTextForSelfService {
  @ValidateNested()
  @Type(() => OnboardingDisabled)
  interactiveOnboarding: OnboardingDisabled;
}

export class CustomChannelConfiguration {
  customChannelId: string;
  recipientAddress: string;
}

class Configuration {
  @IsString()
  @IsNotEmpty()
  alignment: ALIGNMENT;

  @ValidateIf(props => get(props, 'theme.dropshadowEnabled') === false)
  @ValidateNested()
  @Type(() => Border)
  border?: Border;

  @IsString()
  borderRadius?: BORDER_RADIUS;

  @ValidateNested({ each: true })
  @Type(() => DisplayRules)
  displayRules: DisplayRules;

  @ValidateNested()
  @Type(
    getTypeDependsOnWidgetType(['widgetType', 'onboarding.enabled'], ([widgetType, onboardingEnabled]) =>
      widgetType === SELF_SERVICE_WIDGET_TYPE
        ? onboardingEnabled
          ? CustomTextForSelfService
          : CustomTextForSelfServiceWithoutOnboarding
        : onboardingEnabled
        ? CustomTextForChat
        : CustomText
    )
  )
  customText: CustomTextForSelfService | CustomTextForChat;

  colors?: Colors;

  @ValidateNested()
  @Type(() => CloseButton)
  closeButton: CloseButton;

  @ValidateNested()
  @Type(() => InitialChatButton)
  initialChatButton?: InitialChatButton;

  @IsBoolean()
  enabled?: boolean;

  @ValidateNested()
  @ValidateIf(props => get(props, 'widgetType') === SELF_SERVICE_WIDGET_TYPE)
  @Type(() => SelfService)
  selfService: SelfService;

  @ValidateNested()
  @Type(() => BusinessOfficeHours)
  officeHours: BusinessOfficeHours;

  @ValidateNested()
  @Type(() => Theme)
  theme: Theme;

  @ValidateNested()
  @Type(() => PrivacyDisclaimer)
  privacyDisclaimer: PrivacyDisclaimer;

  @ValidateNested()
  @Type(() => MinimizedChatButton)
  minimizedChatButton: MinimizedChatButton;

  @ValidateNested()
  @Type(() => Onboarding)
  onboarding: Onboarding;

  @ValidateNested()
  @Type(() => OngoingChat)
  ongoingChat: OngoingChat;

  @ValidateNested()
  @IsOptional()
  @Type(() => PushNotificationAccountId)
  pushNotificationAccountIds: PushNotificationAccountId;

  @ValidateNested()
  @Type(() => Throttling)
  throttling: Throttling;

  @IsNumber()
  @IsNotEmpty()
  zIndex: number;
}

class MessageAutomationConfiguration {
  @IsString()
  targetType: TARGET_TYPE;
}

class PushNotificationAccountId {
  @ValidateIf(o => !!o.IOS)
  @IsString()
  IOS?: string;

  @ValidateIf(o => !!o.ANDROID)
  @IsString()
  ANDROID?: string;
}

export class Throttling {
  @IsBoolean()
  autoThrottlingEnabled: boolean;

  @Max(3600, { message: 'Please enter a number of seconds that’s less than 1 hour (3600 seconds)' })
  @Min(0, { message: 'Please enter a number of seconds that’s 0 or greater' })
  @IsInt({ message: 'Please enter a whole number of seconds' })
  autoThrottlingThreshold: number;

  @Max(100, { message: 'Please enter a percentage that’s 100 or less' })
  @Min(0, { message: 'Please enter a percentage that’s 0 or higher' })
  @IsInt({ message: 'Please enter a whole number' })
  percentageRollout: number;
}

class CloseButton {
  @IsString()
  @IsNotEmpty()
  title: string;
}

class DisplayRules {
  showByDefault: boolean;
  @ValidateNested({ each: true })
  @Type(() => Rule)
  rules: Rule[];
}

class Rule {
  id: string;
  @IsString()
  @IsNotEmpty()
  type: RULE_TYPE;
  @IsNumber()
  displayDelay: number;
  @ValidateNested()
  @Type(() => UrlRuleContent)
  content: UrlRuleContent;
}

class UrlRuleContent {
  @IsString()
  @IsNotEmpty()
  matchType: URL_RULE_MATCH_TYPE;
  @IsString()
  @IsNotEmpty()
  value: string;
}

class MinimizedChatButton {
  @IsString()
  @IsNotEmpty()
  title: string;
}

class Onboarding {
  @IsBoolean()
  enabled: boolean;

  @ValidateIf(props => get(props, 'enabled') === true)
  @IsString()
  @IsNotEmpty()
  initialCompositionStage: COMPOSITION_STAGE;

  @ValidateIf(props => get(props, 'userResponseType') === 'QUICK_REPLY_RESPONSE')
  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => OnboardingMessage)
  messages: OnboardingMessage[];

  @ValidateIf(props => get(props, 'userResponseType') === 'QUICK_REPLY_RESPONSE')
  @IsBoolean()
  showQuickReplyButtonsWhenChatIsEnded: boolean;
}
class QuickReplyOption {
  @IsString()
  @MaxLength(50, { message: 'Please shorten the button ID to less than 50 characters' })
  @Matches(/^[a-zA-Z0-9_-]*$/i, {
    message: 'Invalid ID. Please use alphanumeric characters including dashes and underscores.',
  })
  @IsNotEmpty({ message: 'Please add a button ID' })
  buttonId: string;

  @IsString()
  @IsNotEmpty({ message: 'Please add a label' })
  @MaxLength(50, { message: 'Please shorten the label to less than 50 characters' })
  label: string;
}
class QuickReplyContent {
  @ValidateNested({ each: true })
  @Type(() => QuickReplyOption)
  options: QuickReplyOption[];
}
class OnboardingMessage {
  @IsEnum(USER_RESPONSE_TYPE)
  subType: USER_RESPONSE_TYPE;

  @ValidateIf(props => get(props, 'subType') === 'QUICK_REPLY_REQUEST')
  @ValidateNested()
  @Type(() => QuickReplyContent)
  subTypeContent: QuickReplyContent;
}
class OngoingChat {
  @IsString()
  @IsNotEmpty({ message: 'Please enter a message.' })
  endMessageAgentEnded: string;

  @IsString()
  @IsNotEmpty({ message: 'Please enter a message.' })
  endMessageTimeExpired: string;

  @IsString()
  @IsNotEmpty({ message: 'Please enter a message.' })
  endMessageNewSessionStarted: string;

  @IsString()
  @IsNotEmpty({ message: 'Please enter a message.' })
  endMessageConsumerNoReply: string;

  @IsString()
  @IsNotEmpty()
  submitButtonTitle: string;
}

class Theme {
  @IsBoolean()
  dropshadowEnabled: boolean;

  @ValidateNested()
  @Type(() => FontLibrary)
  @IsNotEmpty()
  fontLibrary: FontLibrary;

  @ValidateNested()
  @Type(() => Colors)
  @IsNotEmpty()
  colors: Colors;
}

const HEX_COLOR_OPTIONS: Readonly<any> = Object.freeze({ message: 'You need to enter a valid HEX code' });

class Colors {
  @IsHexColor(HEX_COLOR_OPTIONS)
  @IsNotEmpty()
  alert: string;

  @IsHexColor(HEX_COLOR_OPTIONS)
  @IsNotEmpty()
  brandAccent: string;

  @IsHexColor(HEX_COLOR_OPTIONS)
  @IsNotEmpty()
  brandPrimary: string;

  @IsHexColor(HEX_COLOR_OPTIONS)
  @IsNotEmpty()
  brandSecondary: string;

  @IsHexColor(HEX_COLOR_OPTIONS)
  @IsNotEmpty()
  chatLauncher: string;

  @IsHexColor(HEX_COLOR_OPTIONS)
  @IsNotEmpty()
  submitArrow: string;
}

class FontLibrary {
  @IsString()
  @IsNotEmpty()
  primary: string;

  @IsString()
  @IsNotEmpty()
  secondary: string;
}

class Border {
  @IsString()
  @IsNotEmpty()
  width: string;

  @IsHexColor(HEX_COLOR_OPTIONS)
  @IsNotEmpty()
  color: string;
}

class OnboardingDisabled {
  @IsString()
  @IsNotEmpty({ message: 'Please enter a message.' })
  emailPrompt: string;

  @IsString()
  @IsNotEmpty({ message: 'Please enter a message.' })
  namePrompt: string;
}

class OnboardingEnabled extends OnboardingDisabled {
  @IsString()
  @IsNotEmpty({ message: 'Please enter a message.' })
  autoResponse: string;

  @IsString()
  @IsNotEmpty({ message: 'Please enter a message.' })
  onboardingSuccess: string;
}

class PrivacyDisclaimer {
  @IsBoolean()
  isActivated: boolean;

  @IsString()
  disclaimerBody: string;
}

class CustomTextHeaderForChat {
  @IsString()
  @IsNotEmpty()
  greeting: string;
}

class CustomTextHeader {
  @IsString()
  @IsNotEmpty()
  backButton: string;

  @IsString()
  @IsNotEmpty()
  greeting: string;

  @IsString()
  @IsNotEmpty()
  title: string;
}

class ChatHistory {
  @IsNotEmpty()
  attachmentUploadButtonTitle: string;
}

class Composition {
  @IsString()
  @IsNotEmpty()
  messagePrompt: string;
}

class InitialChatButton {
  @ValidateIf(props => get(props, 'type') === ICON_TEXT)
  @IsNotEmpty()
  value?: string;
}

class SelfService {
  @QuickActionsAreValid()
  @ValidateNested({ each: true })
  @Type(() => QuickAction)
  quickActions?: QuickAction[];
}

class QuickAction {
  @IsNotEmpty()
  name: string;

  @ValidateNested()
  @Type(() => QuickActionContent)
  content: QuickActionContent;
}

class QuickActionContent {
  @IsNotEmpty()
  type?: string;

  @ActionValueIsValid()
  value?: string;
}

const endpointsResource = createCollectionResource<Endpoint>({
  name: 'endpoints',
  endpoint: 'v1/orgs/:orgId/configuration/endpoints/:id',
  type: Endpoint,
  observerCreator: ({ actions }) => {
    return ({ dispatch }) => {
      return {
        onBroadcast(item) {
          dispatch(actions.broadcast(item));
        },
        onBroadcastDelete(_: any, params) {
          dispatch(actions.broadcastDelete(params.id));
        },
        onReplaceError(err) {
          dispatch(actions.updateRequestError());
          if (err instanceof DomainError) {
            dispatch(actions.validationError([...translateErrors(err.errors)]));
          }
        },
      };
    };
  },
  options: {
    transientAttrsFn: <Endpoint>(endpoint: Partial<Endpoint>): Endpoint =>
      omit(cloneDeep(endpoint), [
        'isMobile',
        'updatedAtString',
        'updatedByString',
        'createdAt',
        'updatedAt',
      ]) as Endpoint,
  },
});

const validateChat: sharedTypes.AppThunk = attrs => async (dispatch, getState) => {
  let errors: DomainErrorInterface[] = [];
  const dispatchError = (errors: DomainErrorInterface[]): boolean => {
    if (errors.length) {
      dispatch(endpointsResource.actions.validationError(errors));
      return false;
    }

    return true;
  };

  try {
    validateType(Endpoint, attrs);
  } catch (err) {
    errors = [...translateErrors((err as any).errors)];
  }

  let quickReplyErrors: DomainErrorInterface[];

  try {
    quickReplyErrors = await validateQuickReply(attrs);
  } catch (err) {
    dispatch(endpointsResource.actions.updateRequestError({}));
    dispatch(toastError((err as any).message));
    return false;
  }

  if (attrs.type === 'CHAT' && attrs.configuration.widgetType === CHAT_WIDGET_TYPE) {
    return dispatchError(errors);
  }

  let answerErrors: DomainErrorInterface[];

  try {
    answerErrors = attrs.isMobile ? [] : await validateChatQuickActions(attrs, dispatch, getState);
  } catch (err) {
    dispatch(endpointsResource.actions.updateRequestError({}));
    dispatch(toastError((err as any).message));
    return false;
  }

  return dispatchError([...errors, ...answerErrors, ...quickReplyErrors]);
};

endpointsResource.effects.validateChat = validateChat;

export default endpointsResource;
